import { useEffect } from 'react';
import type { RefObject } from 'react';

interface UseDismissOptions {
    open: boolean;
    onClose: () => void;
    triggerRef: RefObject<HTMLElement | null>;
    panelRef: RefObject<HTMLElement | null>;
    /** Return focus to the trigger when closing via Escape (default true). */
    returnFocus?: boolean;
    /**
     * Close on the document-level Escape listener (default true). The combobox
     * wrapper sets this false because its focused input owns Escape directly
     * (e.g. to clear the query as well as close), avoiding a double handler.
     */
    closeOnEscape?: boolean;
}

/**
 * Headless open/close dismissal shared by every dropdown wrapper: a single
 * `mousedown` outside-click listener, Escape-to-close, and focus-return to the
 * trigger on Escape. No DOM/ARIA of its own. Replaces the per-surface hand-rolled
 * outside-click effects and adds the Escape + focus-return they lacked.
 */
export function useDismiss({ open, onClose, triggerRef, panelRef, returnFocus = true, closeOnEscape = true }: UseDismissOptions): void {
    useEffect(() => {
        if (!open) return;

        const onPointerDown = (e: MouseEvent) => {
            const target = e.target as Node | null;
            if (!target) return;
            if (panelRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
            onClose();
        };

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                onClose();
                if (returnFocus) triggerRef.current?.focus();
            }
        };

        document.addEventListener('mousedown', onPointerDown);
        if (closeOnEscape) document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            if (closeOnEscape) document.removeEventListener('keydown', onKeyDown);
        };
    }, [open, onClose, triggerRef, panelRef, returnFocus, closeOnEscape]);
}
